import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { useCallback } from 'react';

import { queryKeys } from '~/api/queryKeys';
import { usePipelinePreview } from '~/features/experiments/hooks/use-experiment-pipeline';
import { useStopExperiment } from '~/features/experiments/hooks/use-experiment-stop';
import { useExperiment } from '~/features/experiments/hooks/use-experiments';
import { useCreateExperimentStore } from '~/features/experiments/store/create-experiment.store';
import {
  ExperimentSplitMode,
  ExperimentStatus,
} from '~/features/experiments/types/experiment.types';
import { useConnectedConnectors } from '~/features/onboarding/hooks/use-connections';

import {
  useExperimentPipelineStatus,
  useTriggerExperimentPipeline,
} from '../hooks/use-content-scoring';
import type { EnrichmentContext } from '../types/enrichment-context.types';
import { combineExperimentFiltersForOverview } from '../utils/combine-experiment-filters';
import { normalizeRunStatus } from '../utils/normalize-run-status';
import {
  FUNNEL_PATH_BY_STEP,
  FUNNEL_STEP,
  resolvePhaseTarget,
  resolveStepIndex,
  type FunnelStepIndex,
} from '../utils/phase-funnel-mapping';

/**
 * Builds the {@link EnrichmentContext} for the experiment content-enrichment
 * flow. Encapsulates all experiment-specific concerns — phase mapping,
 * connection resolution, filter combination, split/pipeline triggers, and
 * terminal-session detection — so the layout can remain host-agnostic.
 *
 * Must be rendered inside the experiment route so `useParams` can resolve
 * `expId` from `/_authenticated/_tenant/experiments_/$expId/content-enrichment`.
 */
export function useExperimentEnrichmentContext(): EnrichmentContext {
  const { expId } = useParams({
    from: '/_authenticated/_tenant/experiments_/$expId/content-enrichment',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  const { data: experiment } = useExperiment(expId);
  const { data: connectedConnectors } = useConnectedConnectors();

  // Resolve the GMC catalog connection. Adapters for other platforms (Meta,
  // Shopify) would look up a different connector code here.
  const gmcConnection = connectedConnectors?.find((c) => c.code === 'GOOGLE_MERCHANT');
  const connectionId = gmcConnection?.connectionId ?? '';
  const connectorCode = gmcConnection?.code ?? null;
  // Tri-state: null while connectedConnectors hasn't resolved yet, so the
  // score page doesn't flash a "not connected" placeholder before the first
  // fetch lands. Once resolved, false covers both "no GMC connection" and
  // "connected but not ACTIVE" (PENDING/PAUSED/INVALID/ERROR).
  const isConnectionActive = connectedConnectors
    ? (gmcConnection?.connectionStatus ?? null) === 'ACTIVE'
    : null;

  // Pre-merge scope + grouping filters into the wire shape the summary endpoint
  // expects. Falls back to an empty map while experiment data is in-flight;
  // the layout awaits both connectionId and filters before enabling the query.
  const filters = experiment ? combineExperimentFiltersForOverview(experiment) : {};

  // Pipeline status + preview — gated to Review & Refine onward so we don't
  // 404-poll the endpoints during the long combined loading screen (step 0),
  // where no run exists yet. The "Generate Full Catalog" CTA now lives on Review
  // & Refine, so its preview/blocking signals must be live from that step.
  const currentStepIndex = resolveStepIndex(pathname);
  const pipelineEnabled = currentStepIndex >= FUNNEL_STEP.REVIEW;
  const { data: pipelineStatusData } = useExperimentPipelineStatus(expId, pipelineEnabled);

  const isGenerateFullCatalogBlocked =
    pipelineStatusData?.status === 'IN_PROGRESS' ||
    pipelineStatusData?.status === 'AWAITING_APPROVAL';

  // Pre-trigger candidate-count forecast — gates the Generate Full Catalog CTA
  // (Review & Refine) when there's nothing to enrich, and surfaces a warning
  // banner when the candidate pool is small enough that the A/B split + outlier
  // filter will likely consume all of it (see staging incident on experiment
  // 6f268c6b, 2026-06-09 — 1 candidate produced 0 enriched rows). Gated to
  // Review & Refine onward so we don't load it during the combined-loading splash.
  const { data: pipelinePreviewData } = usePipelinePreview(expId, { enabled: pipelineEnabled });

  // PostScoring stage completion — layout uses this to prevent premature
  // advancement from Full Catalog (Step 3) to Enrichment Overview (Step 4)
  // while PostScoring is still running after AWAITING_APPROVAL lands.
  const postScoringComplete =
    pipelineStatusData?.stages?.some((s) => s.name === 'PostScoring' && s.status === 'completed') ??
    false;

  const triggerPipeline = useTriggerExperimentPipeline();

  // "Generate Full Catalog" on Review & Refine: fire the end-to-end pipeline.
  // No sensitivity is passed — the split already ran at content-scoring time, and
  // the operator's outlier-sensitivity choice is captured later (Review Groups &
  // Outliers) and sent with the launch approval (see content-enrichment-agent-panel).
  const onGenerateFullCatalog = useCallback(
    (goNext: () => void) => {
      // Guard against a double-fire while the trigger is in flight — the CTA also
      // disables on isGenerateFullCatalogPending, but the pipeline-status query can
      // lag the click, so block here too (mirrors the Meta adapter).
      if (triggerPipeline.isPending) return;
      triggerPipeline.mutate({ expId }, { onSuccess: goNext });
    },
    [expId, triggerPipeline],
  );

  // "Continue to Review Groups & Outliers" on Enrichment Overview: navigate to
  // the groups step. The split is triggered automatically by the backend.
  const onContinueToGroups = useCallback((goNext: () => void) => {
    goNext();
  }, []);

  const navigateToStep = useCallback(
    (step: FunnelStepIndex, replace = false) => {
      void navigate({ to: FUNNEL_PATH_BY_STEP[step], params: { expId }, replace });
    },
    [expId, navigate],
  );

  const navigateOnExit = useCallback(() => {
    void navigate({ to: '/experiments/$id', params: { id: expId }, replace: true });
  }, [expId, navigate]);

  // Zero-eligible-products guard: stop this experiment (CANCELLED — terminates
  // its pipeline + child workflows and flips experiment.status), then send the
  // user back to the start of the create flow to redo product selection. Uses
  // the experiment stop endpoint (`/experiments/{id}/stop`), NOT the enrichment
  // archive endpoint — `expId` is an experiment id, and experiments live in a
  // separate table from enrichment_jobs (archive would 404). Only navigate once
  // the stop succeeds so we never strand the user on a still-running experiment.
  const stopExperiment = useStopExperiment();
  const onInvalidateAndRestart = useCallback(() => {
    stopExperiment.mutate(
      {
        expId,
        payload: {
          finalStatus: 'CANCELLED',
          reason: 'No eligible products — restarting from product selection',
        },
      },
      {
        onSuccess: () => {
          // Start a clean create flow: reset the persisted wizard (so the prior
          // experiment's grouping / draftId / step don't carry over), then
          // re-seed the channel so step 1 can immediately auto-select item-group
          // and load its products — same setup as the normal "New Experiment"
          // path (which resets then setChannel before entering the wizard).
          const store = useCreateExperimentStore.getState();
          store.reset();
          if (connectionId && connectorCode) {
            store.setChannel({ connectionId, code: connectorCode });
          }
          void navigate({ to: '/experiments/create/configure' });
        },
      },
    );
  }, [expId, connectionId, connectorCode, stopExperiment, navigate]);

  // Flushes the experiment detail cache after the full-catalog pipeline completes
  // so the layout's stepper picks up the updated phase (ENRICHMENT_OVERVIEW).
  const onPipelineComplete = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(expId) });
  }, [expId, queryClient]);

  return {
    sessionId: expId,
    connectionId,
    isConnectionActive,
    filters,
    syncId: experiment?.syncId ?? null,
    currentPhase: experiment?.experimentPhase ?? null,
    phaseToStep: resolvePhaseTarget,
    postScoringComplete,
    navigateToStep,
    navigateOnExit,
    onInvalidateAndRestart,
    isInvalidating: stopExperiment.isPending,
    hasSplit: true,
    onGenerateFullCatalog,
    isGenerateFullCatalogBlocked,
    isGenerateFullCatalogPending: triggerPipeline.isPending,
    onContinueToGroups,
    // Spread the preview field only when defined — the context type uses
    // exactOptionalPropertyTypes so undefined isn't assignable to an optional
    // key, but omitting the key is.
    ...(pipelinePreviewData
      ? {
          pipelinePreview: {
            candidateCount: pipelinePreviewData.candidateCount,
            recommendation: pipelinePreviewData.recommendation,
            reason: pipelinePreviewData.recommendationReason,
          },
        }
      : {}),
    isTerminalSession:
      experiment?.status === ExperimentStatus.CANCELLED ||
      experiment?.status === ExperimentStatus.REJECTED,
    skuCount: experiment?.skuCount ?? 0,
    isRandomFallback: experiment?.splitMode === ExperimentSplitMode.RANDOM_FALLBACK,
    onPipelineComplete,
    // Pipeline-run controls for the shared rail. `workflowId` comes from the
    // pipeline-status poll (gated to Review & Refine onward), so the rail stays
    // hidden on the earlier steps. Reset reuses the existing pipeline trigger.
    workflowControls: {
      workflowId: pipelineStatusData?.workflowId ?? null,
      runStatus: normalizeRunStatus(pipelineStatusData?.status),
      onReset: () => {
        if (!triggerPipeline.isPending) triggerPipeline.mutate({ expId });
      },
      isResetting: triggerPipeline.isPending,
      onControlSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.experiments.pipelineStatus(expId),
        });
      },
    },
  };
}
